import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getData, API_BASE_URL } from '../Services/api';
import { toast } from 'react-toastify';

import Pagination from '../Services/Pagination';

const formatInvoiceDate = (dateVal) => {
    if (!dateVal || dateVal === '-') return '-';
    if (typeof dateVal === 'string' && /^[0-9]{1,2}-[A-Za-z]{3}-[0-9]{2,4}$/.test(dateVal)) return dateVal;
    try {
        const str = String(dateVal);
        const parsed = new Date(str.includes('T') ? str : `${str}T00:00:00`);
        if (isNaN(parsed.getTime())) return str;
        return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }).replace(/ /g, '-');
    } catch {
        return dateVal;
    }
};

const InvoicingDataLog = () => {
    const [filters, setFilters] = useState({
        from: '',
        to: '',
        userId: 'all',
        status: 'all',
        search: ''
    });
    
    const [users, setUsers] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await getData('/reports/invoicing-users');
                if (res.success && res.data) {
                    setUsers(res.data);
                }
            } catch (err) {
                console.log(err);
            }
        };
        fetchUsers();
    }, []);

    const handleExportExcel = async (orderId, invoiceNumber, poNumber) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}proforma-orders/export-excel/${orderId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!res.ok) throw new Error("Failed to export Excel");

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Invoice_${invoiceNumber || poNumber}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success("Excel Downloaded successfully!");
        } catch (error) {
            console.error("Excel Export Error:", error);
            toast.error("Failed to export Excel file.");
        }
    };

    const fetchLogData = async (pageNumber = page) => {
        setIsLoading(true);
        try {
            const query = new URLSearchParams({
                from: filters.from,
                to: filters.to,
                userId: filters.userId,
                status: filters.status,
                search: filters.search,
                page: pageNumber,
                limit: 10
            }).toString();

            const res = await getData(`/reports/invoicing-data-log?${query}`);
            if (res.success) {
                setData(res.data || []);
                setPage(res.currentPage || 1);
                setPages(res.totalPages || 1);
                setTotal(res.total || 0);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Debounce search slightly if we want, or just fetch on every change
        const delay = setTimeout(() => {
            fetchLogData(1);
        }, 300);
        return () => clearTimeout(delay);
    }, [filters]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber !== page) {
            fetchLogData(pageNumber);
        }
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            // Build query string
            const query = new URLSearchParams({
                from: filters.from,
                to: filters.to,
                userId: filters.userId,
                status: filters.status,
                search: filters.search
            }).toString();

            // Get token
            const token = localStorage.getItem('token');
            
            // We can fetch it as blob
            const response = await fetch(`${API_BASE_URL}reports/invoicing-data-log/download?${query}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Invoicing_Data_Log_${new Date().getTime()}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } else {
                alert("Failed to download report");
            }
        } catch (err) {
            console.error(err);
            alert("Error downloading report");
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="pc-container">
            <div className="pc-content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-block">
                        <div className="row align-items-center">
                            <div className="col-md-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/">Home</Link>
                                    </li>
                                    <li className="breadcrumb-item" aria-current="page">
                                        Invoicing Data Log
                                    </li>
                                </ul>
                            </div>
                            <div className="col-md-12">
                                <div className="page-header-title">
                                    <h2 className="mb-0">Invoicing Data Log</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="row mb-3">
                    <div className="col-md-2">
                        <label>Start Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.from}
                            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                        />
                    </div>
                    <div className="col-md-2">
                        <label>End Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.to}
                            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                        />
                    </div>
                    <div className="col-md-2">
                        <label>Status</label>
                        <select
                            className="form-control"
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        >
                            <option value="all">All Status</option>
                            <option value="invoiced">Invoiced</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label>User Name</label>
                        <select
                            className="form-control"
                            value={filters.userId}
                            onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                        >
                            <option value="all">All Users</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>{u.username || u.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label>Customer Name</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search Customer..."
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12 mt-3 mb-3 d-flex justify-content-end">
                        <button 
                            className="btn btn-primary" 
                            onClick={handleDownload}
                            disabled={isDownloading}
                        >
                            {isDownloading ? (
                                <span><span className="spinner-border spinner-border-sm mr-2"></span> Downloading...</span>
                            ) : (
                                <span><i className="fas fa-download mr-2"></i> Download Excel Data Log</span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card table-card">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover" id="pc-dt-simple">
                                        <thead>
                                            <tr>
                                                <th>Company Name</th>
                                                <th>Company Type</th>
                                                <th>Invoice Number</th>
                                                <th>Invoice Date</th>
                                                <th>Invoice Time</th>
                                                <th>User Name</th>
                                                <th>Customer Name</th>
                                                <th>Product Name</th>
                                                <th>Product Code</th>
                                                <th>Qty</th>
                                                <th>Units</th>
                                                <th>Rate</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th>Additional Comments</th>
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isLoading ? (
                                                <tr>
                                                    <td colSpan={16} className="text-center">Loading...</td>
                                                </tr>
                                            ) : data.length > 0 ? (
                                                data.map((order, index) => {
                                                    const isCancelled = String(order.status).toLowerCase() === 'cancelled';
                                                    return (
                                                        <tr key={order.id}>
                                                            <td>{order.company_name}</td>
                                                            <td>{order.company_type}</td>
                                                            <td>{order.invoice_number || '-'}</td>
                                                            <td>{formatInvoiceDate(order.invoice_date)}</td>
                                                            <td>{order.invoice_time || '-'}</td>
                                                            <td>{order.user_name}</td>
                                                            <td>{order.customer_name}</td>
                                                            <td>{order.product_name}</td>
                                                            <td>{order.product_code}</td>
                                                            <td>{order.qty}</td>
                                                            <td>{order.units}</td>
                                                            <td>{order.rate}</td>
                                                            <td>{order.amount}</td>
                                                            <td>
                                                                <span className={`badge ${isCancelled ? 'bg-light-danger text-danger' : 'bg-light-success text-success'}`}>
                                                                    {order.status || '-'}
                                                                </span>
                                                            </td>
                                                            <td>{order.additional_comments || '-'}</td>
                                                            <td className="text-center">
                                                                <button 
                                                                    className="btn btn-sm btn-warning"
                                                                    title="Export Excel"
                                                                    onClick={() => handleExportExcel(order.order_id, order.invoice_number, order.po_number)}
                                                                >
                                                                    <i className="fas fa-file-excel" /> Excel
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={16} className="text-center">No Invoicing Data Log Found.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                {/* Pagination */}
                                {total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center px-4 pb-3">
                                        <div className="text-muted">
                                            Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, total)} of {total} entries
                                        </div>
                                        <Pagination page={page} pages={pages} onPageChange={handlePageChange} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default InvoicingDataLog;
